import { useEffect, useState, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useGlobalContext } from "../../contexts/GlobalContext";
import { getDashboardSummary } from "../../queries/dashboard";

// Register Chart.js components and the datalabels plugin
Chart.register(...registerables, ChartDataLabels);

const LeadsChart = (props) => {
    const [state] = useGlobalContext();
    const [chartHeight, setChartHeight] = useState(320);
    const [summaryData, setSummaryData] = useState([]);
    const chartRef = useRef(null);
    const { onDataUpdate } = props;

    let noOfDays = 0;
    let groupByWeek = false;
    let groupByMonth = false;

    if (props.timeRange === '7 Days') {
        noOfDays = 7;
    } else if (props.timeRange === '1 Month') {
        noOfDays = 30;
    } else if (props.timeRange === '6 Months') {
        noOfDays = 180;
        groupByMonth = true;
    }

    useEffect(() => {
        const fetchSummaryData = async () => {
            const data = await getDashboardSummary();
            if (!data) return;

            if (Array.isArray(data)) {
                setSummaryData(data);
            } else if (typeof data === 'string') {
                const csvLines = data.trim().split('\n');
                const parsedData = csvLines.map(line => {
                    const fields = [];
                    let current = '';
                    let inQuotes = false;
                    for (let i = 0; i < line.length; i++) {
                        const char = line[i];
                        if (char === '"') { inQuotes = !inQuotes; }
                        else if (char === ',' && !inQuotes) { fields.push(current); current = ''; }
                        else { current += char; }
                    }
                    fields.push(current);
                    return {
                        firstName: fields[0]?.replace(/"/g, '') || '',
                        lastName: fields[1]?.replace(/"/g, '') || '',
                        email: fields[2]?.replace(/"/g, '') || '',
                        phone: fields[3]?.replace(/"/g, '') || '',
                        country: fields[4]?.replace(/"/g, '') || '',
                        program: fields[5]?.replace(/"/g, '') || '',
                        year: fields[6]?.replace(/"/g, '') || '',
                        type: fields[7]?.replace(/"/g, '') || '',
                        createdAt: fields[8]?.replace(/"/g, '') || ''
                    };
                });
                setSummaryData(parsedData);
            }
        };

        fetchSummaryData();
    }, []);

    useEffect(() => {
        // Don't process if data hasn't loaded yet
        if (!summaryData || summaryData.length === 0) {
            console.log("Waiting for summary data to load...");
            return;
        }

        // console.log("Summary data loaded:", summaryData.length, "leads");

        // Filter data by program access
        let dataToCount = summaryData;

        // Apply program filtering
        if (state.userProgramAccess?.role === "program_user" &&
            state.userProgramAccess?.assignedPrograms?.length > 0) {

            // Debug: Log all unique program values to help identify correct codes
            const uniquePrograms = [...new Set(summaryData.map(lead => lead.program))].filter(p => p);
            // console.log("CHART - Available programs in data:", uniquePrograms);
            // console.log("CHART - User's assigned programs:", state.userProgramAccess.assignedPrograms);

            dataToCount = summaryData.filter(lead =>
                state.userProgramAccess.assignedPrograms.includes(lead.program)
            );

            console.log("CHART - Filtered lead count:", dataToCount.length, "out of", summaryData.length);
            console.log("CHART - Filtered leads:", dataToCount.map(lead => ({ id: lead.id, program: lead.program, createdAt: lead.createdAt })));
        } else {
            console.log("CHART - Showing all data (no filtering):", dataToCount.length, "leads");
        }

        let dateLabels = [];
        let leadCountsPerPeriod = [];

        if (groupByMonth) {
            // Group by months for 6 months view
            const numberOfMonths = 6;
            const today = new Date();

            // Generate month labels
            for (let i = 0; i < numberOfMonths; i++) {
                const monthDate = new Date(today.getFullYear(), today.getMonth() - (numberOfMonths - 1 - i), 1);
                const formattedLabel = monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                dateLabels.push(formattedLabel);
            }

            const countLeadsByMonth = (leadsData) => {
                const monthCounts = new Array(numberOfMonths).fill(0);

                for (let i = 0; i < numberOfMonths; i++) {
                    const monthStart = new Date(today.getFullYear(), today.getMonth() - (numberOfMonths - 1 - i), 1);
                    const monthEnd = new Date(today.getFullYear(), today.getMonth() - (numberOfMonths - 1 - i) + 1, 0, 23, 59, 59, 999);

                    leadsData?.forEach(lead => {
                        if (lead.createdAt) {
                            const leadDate = new Date(lead.createdAt);
                            if (!isNaN(leadDate.getTime())) {
                                if (leadDate >= monthStart && leadDate <= monthEnd) {
                                    monthCounts[i]++;
                                    console.log(`CHART - Month ${i}: Lead ${lead.id} counted for ${monthStart.toLocaleDateString()}`);
                                }
                            }
                        }
                    });
                }
                return monthCounts;
            };

            leadCountsPerPeriod = countLeadsByMonth(dataToCount);
        } else {
            // Daily view for 7 days and 1 month
            const currentDate = new Date();
            for (let i = 0; i < noOfDays; i++) {
                const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - i);
                const formattedDate = date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
                dateLabels.push(formattedDate);
            }

            const countLeadsPerDay = (leadsData) => {
                const leadCounts = new Array(noOfDays).fill(0);
                const today = new Date();

                for (let i = 0; i < noOfDays; i++) {
                    const day = new Date(today.getTime() - (noOfDays - i - 1) * 24 * 60 * 60 * 1000);
                    const dateString = day.toISOString().split('T')[0];

                    leadsData?.forEach(lead => {
                        if (lead.createdAt) {
                            const leadDate = new Date(lead.createdAt);
                            // Check if date is valid
                            if (!isNaN(leadDate.getTime())) {
                                const leadDateString = leadDate.toISOString().split('T')[0];
                                if (leadDateString === dateString) {
                                    leadCounts[i]++;
                                    console.log(`CHART - Day ${i}: Lead ${lead.id} counted for ${dateString}`);
                                }
                            }
                        }
                    });
                }
                return leadCounts;
            };

            leadCountsPerPeriod = countLeadsPerDay(dataToCount);
        }

        // Calculate total leads in the selected period
        const totalLeadsInPeriod = leadCountsPerPeriod.reduce((sum, count) => sum + count, 0);

        // Notify parent component of the total
        if (onDataUpdate) {
            onDataUpdate(totalLeadsInPeriod);
        }

        const data = {
            labels: groupByMonth ? dateLabels : dateLabels.reverse(),
            datasets: [{
                data: leadCountsPerPeriod,
                fill: true,
                backgroundColor: 'rgba(147, 197, 253, 0.3)',
                borderColor: '#3B82F6',
                tension: 0.4
            }]
        };

        const options = {
            layout: {
                padding: {
                    top: 30,
                    bottom: 10,
                    left: -10,
                    right: -10
                }
            },
            scales: {
                x: {
                    display: true,
                    grid: {
                        display: false,
                        drawBorder: false
                    },
                    ticks: {
                        color: '#71717A',
                        font: {
                            size: 11
                        },
                        maxRotation: 0,
                        autoSkip: true,
                        maxTicksLimit: groupByMonth ? 6 : 7
                    }
                },
                y: {
                    display: true,
                    beginAtZero: true,
                    grace: '10%',
                    grid: {
                        color: '#E5E7EB',
                        drawBorder: false
                    },
                    ticks: {
                        color: '#71717A',
                        font: {
                            size: 11
                        },
                        padding: 10
                    }
                }
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    backgroundColor: "#FFFFFF",
                    titleColor: "#000000",
                    bodyColor: "#000000",
                    borderColor: '#E4E4E7',
                    borderWidth: 1,
                    callbacks: {
                        title: function(context) {
                            if (groupByMonth) {
                                // Show month name for 6 months view
                                return context[0].label;
                            } else {
                                return context[0].label;
                            }
                        },
                        label: function(context) {
                            return 'Leads: ' + context.parsed.y;
                        }
                    }
                },
                datalabels: {
                    display: function(context) {
                        // Only show label when hovering
                        return context.active;
                    },
                    align: 'top',
                    anchor: 'end',
                    color: '#3B82F6',
                    font: {
                        weight: '600',
                        size: 12
                    },
                    formatter: function(value) {
                        return value > 0 ? value : '';
                    },
                    offset: 6,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 4,
                    padding: {
                        top: 4,
                        bottom: 4,
                        left: 6,
                        right: 6
                    }
                }
            },
            elements: {
                point: {
                    radius: 4,
                    backgroundColor: '#3B82F6',
                    borderColor: '#FFFFFF',
                    borderWidth: 2,
                    hoverRadius: 6,
                    hoverBackgroundColor: '#3B82F6',
                    hoverBorderColor: '#FFFFFF',
                    hoverBorderWidth: 2,
                },
            },
            maintainAspectRatio: false
        };

        const config = {
            type: 'line',
            data: data,
            options: options
        };

        const ctx = chartRef.current;
        if (!ctx) return;

        // Get the parent container's dimensions
        const parent = ctx.parentElement;
        const parentWidth = parent ? parent.offsetWidth : 750;

        ctx.width = parentWidth;
        ctx.height = chartHeight;

        const myChart = new Chart(ctx, config);

        return () => myChart.destroy();
    }, [props.timeRange, chartHeight, summaryData, noOfDays, onDataUpdate, groupByMonth, state.userProgramAccess]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 1900) {
                setChartHeight(300);
            } else {
                setChartHeight(300);
            }
        };

        handleResize();
        const chartCanvas = chartRef.current;
        if (chartCanvas) {
            window.addEventListener('resize', handleResize);
        }
        return () => {
            if (chartCanvas) {
                window.removeEventListener('resize', handleResize);
            }
        };
    }, []);

    return (
        <div className='ggggg' style={{
            position: 'relative',
            width: '740px',
            height: `${chartHeight}px`,
            maxWidth: '100%',
            overflow: 'hidden'
        }}>
            <canvas
                id="leadsChart"
                ref={chartRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                }}
            ></canvas>
        </div>
    );
}

export default LeadsChart;
